import { useMemo, useState } from 'react';
import { isWhereGroup, type FilterClause, type FilterOp, type WhereGroup, type WhereNode } from '../api/client';
import { OP_LABELS, opsForType, type FieldMeta, type ResourceMeta } from '../meta/types';
import { Button, IconButton } from '../design/primitives/Button';
import { Chip } from '../design/primitives/Misc';
import { Dialog } from '../design/primitives/Overlay';
import { Input, Select } from '../design/primitives/Input';
import { FieldEditor } from '../fields/registry';
import { RelationPicker, useRelationLabel } from '../fields/RelationPicker';

// The condition builder: any filter a list can express, as nested groups. A group matches ALL
// (and) or ANY (or) of its rows; a row is a condition (field · operator · value) or another group,
// to any depth the API allows (5). It edits the list's `where` (lib/query.ts on the API), which is
// ANDed with the quick filters and kept in the URL and in saved views like every other filter.

export const MAX_DEPTH = 5;

type Group = { mode: 'all' | 'any'; items: Item[] };
type Item = { kind: 'cond'; field: string; op: FilterOp; value: unknown } | { kind: 'group'; group: Group };

const toGroup = (w: WhereGroup | null | undefined): Group => {
  if (!w) return { mode: 'all', items: [] };
  const mode = 'any' in w ? 'any' : 'all';
  const kids = 'any' in w ? w.any : w.all;
  return { mode, items: kids.map((n): Item => (isWhereGroup(n) ? { kind: 'group', group: toGroup(n) } : { kind: 'cond', field: n.field, op: n.op, value: n.value })) };
};

const toWhere = (g: Group): WhereGroup => {
  const kids: WhereNode[] = g.items.map((it) => (it.kind === 'group' ? toWhere(it.group) : ({ field: it.field, op: it.op, value: it.value } as FilterClause)));
  return g.mode === 'any' ? { any: kids } : { all: kids };
};

const filled = (it: Extract<Item, { kind: 'cond' }>) =>
  it.op === 'is_null' ? true : it.op === 'between' ? Array.isArray(it.value) && it.value[0] != null && it.value[0] !== '' && it.value[1] != null && it.value[1] !== '' : it.op === 'in' || it.op === 'nin' ? Array.isArray(it.value) && it.value.length > 0 : it.value != null && it.value !== '';

/** Every condition has a value and no group is empty. */
export function validWhere(g: Group): boolean {
  return g.items.length > 0 && g.items.every((it) => (it.kind === 'group' ? validWhere(it.group) : filled(it)));
}

/** Number of conditions in a tree (the summary chip's count). */
export function countConditions(w: WhereNode | null | undefined): number {
  if (!w) return 0;
  if (!isWhereGroup(w)) return 1;
  return ('any' in w ? w.any : w.all).reduce((n, c) => n + countConditions(c), 0);
}

const emptyValue = (op: FilterOp): unknown => (op === 'is_null' ? true : op === 'in' || op === 'nin' ? [] : op === 'between' ? [null, null] : null);

function MultiValue({ field, value, onChange }: { field: FieldMeta; value: unknown; onChange: (v: unknown) => void }) {
  const arr = Array.isArray(value) ? value.map(String) : [];
  if (field.type === 'enum') {
    return (
      <div className="hf-chiprow">
        {(field.enum ?? []).map((o) => {
          const on = arr.includes(o.value);
          return (
            <Chip key={o.value} active={on} onClick={() => onChange(on ? arr.filter((x) => x !== o.value) : [...arr, o.value])}>
              {o.label}
            </Chip>
          );
        })}
      </div>
    );
  }
  if (field.type === 'relation') {
    return (
      <div className="hf-stack" style={{ gap: 6 }}>
        <div className="hf-chiprow">
          {arr.map((id) => (
            <PickedChip key={id} field={field} id={id} onRemove={() => onChange(arr.filter((x) => x !== id))} />
          ))}
        </div>
        <RelationPicker field={{ ...field, required: false, nullable: true }} value={null} size="sm" aria-label={`Add ${field.label.toLowerCase()}`} onChange={(v) => v && !arr.includes(v) && onChange([...arr, v])} />
      </div>
    );
  }
  return <Input size="sm" placeholder="Comma-separated" aria-label="Values" value={arr.join(',')} onChange={(e) => onChange(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />;
}

function PickedChip({ field, id, onRemove }: { field: FieldMeta; id: string; onRemove: () => void }) {
  const label = useRelationLabel(field.relation, id);
  return <Chip active onRemove={onRemove}>{label.data ?? '…'}</Chip>;
}

function ConditionRow({ meta, fields, item, onChange, onRemove }: { meta: ResourceMeta; fields: FieldMeta[]; item: Extract<Item, { kind: 'cond' }>; onChange: (it: Item) => void; onRemove: () => void }) {
  const field = meta.fields.find((f) => f.name === item.field) ?? fields[0]!;
  const ops = field.ops ?? opsForType(field.type);
  const editor = (v: unknown, set: (x: unknown) => void, label: string) => <FieldEditor field={{ ...field, widget: undefined, required: false, nullable: true }} value={v} onChange={set} size="sm" aria-label={label} />;
  return (
    <div className="hf-cond-row" role="group" aria-label={`Condition on ${field.label}`}>
      <Select
        size="sm"
        aria-label="Field"
        value={field.name}
        onChange={(e) => {
          const f = meta.fields.find((x) => x.name === e.target.value)!;
          const op = (f.ops ?? opsForType(f.type))[0]!;
          onChange({ kind: 'cond', field: f.name, op, value: emptyValue(op) });
        }}
        options={fields.map((f) => ({ value: f.name, label: f.label }))}
      />
      <Select size="sm" aria-label="Operator" value={item.op} onChange={(e) => onChange({ ...item, op: e.target.value as FilterOp, value: emptyValue(e.target.value as FilterOp) })} options={ops.map((o) => ({ value: o, label: OP_LABELS[o] }))} />
      <div className="hf-cond-value">
        {item.op === 'is_null' ? (
          <Select size="sm" aria-label="Value" value={item.value ? 'true' : 'false'} onChange={(e) => onChange({ ...item, value: e.target.value === 'true' })} options={[{ value: 'true', label: 'is empty' }, { value: 'false', label: 'is not empty' }]} />
        ) : item.op === 'between' ? (
          <div className="hf-row">
            {editor((item.value as unknown[])?.[0], (x) => onChange({ ...item, value: [x, (item.value as unknown[])?.[1] ?? null] }), 'From')}
            <span className="hf-muted">and</span>
            {editor((item.value as unknown[])?.[1], (x) => onChange({ ...item, value: [(item.value as unknown[])?.[0] ?? null, x] }), 'To')}
          </div>
        ) : item.op === 'in' || item.op === 'nin' ? (
          <MultiValue field={field} value={item.value} onChange={(v) => onChange({ ...item, value: v })} />
        ) : field.type === 'relation' ? (
          <RelationPicker field={{ ...field, required: false, nullable: true }} value={item.value == null ? null : String(item.value)} size="sm" aria-label="Value" onChange={(v) => onChange({ ...item, value: v })} />
        ) : (
          editor(item.value, (x) => onChange({ ...item, value: x }), 'Value')
        )}
      </div>
      <IconButton icon="x" label="Remove condition" size="sm" onClick={onRemove} />
    </div>
  );
}

function GroupEditor({ meta, fields, group, depth, onChange, onRemove }: { meta: ResourceMeta; fields: FieldMeta[]; group: Group; depth: number; onChange: (g: Group) => void; onRemove?: () => void }) {
  const set = (i: number, it: Item) => onChange({ ...group, items: group.items.map((x, j) => (j === i ? it : x)) });
  const drop = (i: number) => onChange({ ...group, items: group.items.filter((_, j) => j !== i) });
  const first = fields[0]!;
  const firstOp = (first.ops ?? opsForType(first.type))[0]!;
  const addCond = () => onChange({ ...group, items: [...group.items, { kind: 'cond', field: first.name, op: firstOp, value: emptyValue(firstOp) }] });
  const addGroup = () => onChange({ ...group, items: [...group.items, { kind: 'group', group: { mode: group.mode === 'all' ? 'any' : 'all', items: [{ kind: 'cond', field: first.name, op: firstOp, value: emptyValue(firstOp) }] } }] });
  return (
    <div className={['hf-cond-group', depth > 1 && 'nested'].filter(Boolean).join(' ')} role="group" aria-label={depth === 1 ? 'All conditions' : 'Condition group'}>
      <div className="hf-cond-head">
        <span>Match</span>
        <Select size="sm" aria-label="Match" value={group.mode} onChange={(e) => onChange({ ...group, mode: e.target.value as 'all' | 'any' })} options={[{ value: 'all', label: 'all (AND)' }, { value: 'any', label: 'any (OR)' }]} />
        <span className="hf-muted">of these</span>
        {onRemove && <IconButton icon="x" label="Remove group" size="sm" onClick={onRemove} />}
      </div>
      {group.items.length === 0 && <div className="hf-muted" style={{ padding: '4px 0' }}>No conditions yet.</div>}
      {group.items.map((it, i) =>
        it.kind === 'cond' ? (
          <ConditionRow key={i} meta={meta} fields={fields} item={it} onChange={(x) => set(i, x)} onRemove={() => drop(i)} />
        ) : (
          <GroupEditor key={i} meta={meta} fields={fields} group={it.group} depth={depth + 1} onChange={(g) => set(i, { kind: 'group', group: g })} onRemove={() => drop(i)} />
        ),
      )}
      <div className="hf-row" style={{ gap: 4 }}>
        <Button size="sm" variant="ghost" icon="plus" onClick={addCond}>
          Condition
        </Button>
        {depth < MAX_DEPTH && (
          <Button size="sm" variant="ghost" icon="plus" onClick={addGroup}>
            Group
          </Button>
        )}
      </div>
    </div>
  );
}

/** The builder dialog: edit a copy, Apply writes it back (an empty builder clears the conditions). */
export function ConditionBuilder({ meta, where, open, onClose, onApply }: { meta: ResourceMeta; where: WhereGroup | null; open: boolean; onClose: () => void; onApply: (w: WhereGroup | null) => void }) {
  const fields = useMemo(() => meta.fields.filter((f) => f.filterable && f.name !== 'tenant_id' && f.name !== meta.idField && f.type !== 'json'), [meta]);
  const [draft, setDraft] = useState<Group>(() => toGroup(where));
  const ok = draft.items.length === 0 || validWhere(draft);
  if (!fields.length) return null;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Filter ${meta.labelPlural.toLowerCase()} by conditions`}
      description="Combine conditions with AND / OR and nest groups. They apply on top of the quick filters, and are saved with the view."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => setDraft({ mode: 'all', items: [] })}>
            Clear all
          </Button>
          <span className="hf-grow" />
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!ok} onClick={() => onApply(draft.items.length ? toWhere(draft) : null)}>
            Apply
          </Button>
        </>
      }
    >
      <GroupEditor meta={meta} fields={fields} group={draft} depth={1} onChange={setDraft} />
      {!ok && <p className="hf-muted" role="status">Every condition needs a value, and every group at least one condition.</p>}
    </Dialog>
  );
}
